// shared components
import { appInsights } from '@/components/application-insights/ApplicationInsightsService';

/**
 * Logs chat feedback to Application Insights
 * @param {Object} params
 * @param {string} params.userEmail - Email of the user providing feedback
 * @param {string} params.accountId - AccountId from Redux
 * @param {string} params.accountName - Account name from Redux
 * @param {Array} params.messages - Array of chat messages
 * @param {string} params.feedback - 'positive' or 'negative'
 * @param {string} params.provider - AI provider used (ollama, anthropic, etc.)
 * @param {string} params.model - Model name used
 * @param {string} params.sessionId - Unique session identifier
 * @param {string} [params.comments] - Optional additional comments from user
 */
export const logChatFeedback = ({
  userEmail,
  accountId,
  accountName,
  messages,
  feedback,
  provider,
  model,
  sessionId,
  comments,
}) => {
  // Skip logging if Application Insights is not initialized
  if (!appInsights) {
    console.warn(
      'Application Insights is not initialized. Feedback not logged.',
    );
    return;
  }

  // Prepare sanitized messages (remove sensitive data if needed)
  const sanitizedMessages = messages.map((msg, index) => ({
    index,
    role: msg.role,
    partTypes: msg.parts.map((p) => p.type),
    hasTextContent: msg.parts.some((p) => p.type === 'text'),
    hasToolCall: msg.parts.some((p) => p.type === 'tool-call'),
    hasToolResult: msg.parts.some((p) => p.type === 'tool-result'),
    // Include text content (be careful with PII/sensitive data)
    textContent: msg.parts
      .filter((p) => p.type === 'text')
      .map((p) => p.content)
      .join('\n'),
  }));

  console.log('appInsights is initialized. Logging chat feedback:', {
    userEmail,
    accountId,
    accountName,
    feedback,
    provider,
    model,
    sessionId,
    messageCount: messages.length,
  });

  console.log('appInsights instance:', appInsights);

  // Track custom event in Application Insights
  appInsights.trackEvent({
    name: 'ChatFeedback',
    properties: {
      userEmail,
      accountId,
      accountName,
      feedback, // 'positive' or 'negative'
      provider,
      model,
      sessionId,
      messageCount: messages.length,
      conversationLength: messages.length,
      timestamp: new Date().toISOString(),
      // Optional user comments
      comments: comments || '',
      hasComments: !!comments,
      // Store messages as JSON string for querying in App Insights
      messages: JSON.stringify(sanitizedMessages),
    },
  });

  console.log('Chat feedback logged to Application Insights:', {
    feedback,
    userEmail,
    accountId,
    messageCount: messages.length,
  });
};
